import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChartModal from "../../../modals/ChartModal";
import {
  fetchWaagData,
  fetchWaagDataByDateRange,
} from "../../../store/thunks/waagThunk";
import { clearWaagData } from "../../../store/slices/waagSlice";

const WaagChart = () => {
  const dispatch = useDispatch();
  const waagData = useSelector((state) => state.waag.data);
  const dateRangeData = useSelector((state) => state.waag.dateRangeData);
  const loadingStatus = useSelector((state) => state.waag.loading);
  const [metrics, setMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDateRangeActive, setIsDateRangeActive] = useState(false);

  useEffect(() => {
    dispatch(fetchWaagData());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearWaagData());
    };
  }, [dispatch]);

  const handleDateSubmit = (e) => {
    e.preventDefault();
    if (startDate && endDate) {
      dispatch(fetchWaagDataByDateRange({ startDate, endDate }));
      setIsDateRangeActive(true);
      setShowDatePicker(false);
    }
  };

  const handleClearDateRange = () => {
    setStartDate("");
    setEndDate("");
    setIsDateRangeActive(false);
    dispatch(clearWaagData());
  };

  // Transform data for date range view
  const getDateRangeMetrics = () => {
    if (!dateRangeData) return [];

    return [
      {
        name: "Total Revenue",
        value: `$${
          dateRangeData.revenue.dateRangeRevenue?.toFixed(2) || "0.00"
        }`,
        weeklyData: dateRangeData.revenue.weeklyData || [],
      },
      {
        name: "Total Appointments",
        value: dateRangeData.appointments.dateRangeTotal?.toString() || "0",
        weeklyData: dateRangeData.appointments.weeklyData || [],
      },
      {
        name: "Cancelled Appointments",
        value:
          dateRangeData.cancelledAppointments.dateRangeTotal?.toString() || "0",
        weeklyData: dateRangeData.cancelledAppointments.weeklyData || [],
      },
      {
        name: "No Show Appointments",
        value:
          dateRangeData.noShowAppointments.dateRangeTotal?.toString() || "0",
        weeklyData: dateRangeData.noShowAppointments.weeklyData || [],
      },
      {
        name: "Treated Hours",
        value: dateRangeData.treatedHours.dateRangeTotal?.toFixed(1) || "0.0",
        weeklyData: dateRangeData.treatedHours.weeklyData || [],
      },
      {
        name: "New Clients",
        value: dateRangeData.newClients.dateRangeTotal?.toString() || "0",
        weeklyData: dateRangeData.newClients.weeklyData || [],
      },
      {
        name: "Phone Calls",
        value: dateRangeData.phoneCalls.dateRangeTotal?.toString() || "0",
        weeklyData: dateRangeData.phoneCalls.weeklyData || [],
      },
      {
        name: "Intake Appointments",
        value: dateRangeData.appointmentTypes.intake?.toString() || "0",
        weeklyData: [],
      },
      {
        name: "Consult Appointments",
        value: dateRangeData.appointmentTypes.consult?.toString() || "0",
        weeklyData: [],
      },
      {
        name: "Assessment Appointments",
        value: dateRangeData.appointmentTypes.assessment?.toString() || "0",
        weeklyData: [],
      },
    ];
  };

  // Transform data for standard view
  useEffect(() => {
    if (waagData && waagData.revenue) {
      // Transform waagData into the format expected by the component
      const transformedMetrics = [
        {
          name: "Total Revenue",
          thisWeek: dateRangeData
            ? `$${dateRangeData.revenue.dateRangeRevenue?.toFixed(2) || "0.00"}`
            : `$${waagData.revenue.thisWeekRevenue?.toFixed(2) || "0.00"}`,
          lastWeek: dateRangeData
            ? "-"
            : `$${waagData.revenue.lastWeekRevenue?.toFixed(2) || "0.00"}`,
          prevWeek: dateRangeData
            ? "-"
            : `$${waagData.revenue.prevWeekRevenue?.toFixed(2) || "0.00"}`,
          mtd: dateRangeData
            ? "-"
            : `$${waagData.revenue.mtdRevenue?.toFixed(2) || "0.00"}`,
          lastMonth: dateRangeData
            ? "-"
            : `$${waagData.revenue.lastMonthRevenue?.toFixed(2) || "0.00"}`,
          target: "$85,000",
          percentCurr: dateRangeData
            ? "-"
            : `${(
                (waagData.revenue.mtdRevenue / waagData.revenue.target) *
                100
              ).toFixed(0)}%`,
          percentLast: dateRangeData
            ? "-"
            : `${(
                (waagData.revenue.lastMonthRevenue / waagData.revenue.target) *
                100
              ).toFixed(0)}%`,
          weeklyData: dateRangeData
            ? dateRangeData.revenue.weeklyData
            : waagData.revenue.weeklyData?.map((week) => ({
                weekNumber: week.weekNumber,
                year: week.year,
                value: week.value,
                date: week.date,
              })) || [],
        },
        {
          name: "Total Appointments",
          thisWeek: dateRangeData
            ? dateRangeData.appointments.dateRangeTotal?.toString() || "0"
            : waagData.appointments?.thisWeek?.toString() || "0",
          lastWeek: dateRangeData
            ? "-"
            : waagData.appointments?.lastWeek?.toString() || "0",
          prevWeek: dateRangeData
            ? "-"
            : waagData.appointments?.prevWeek?.toString() || "0",
          mtd: dateRangeData
            ? "-"
            : waagData.appointments?.mtd?.toString() || "0",
          lastMonth: dateRangeData
            ? "-"
            : waagData.appointments?.lastMonth?.toString() || "0",
          target: waagData.appointments?.target?.toString() || "0",
          percentCurr: dateRangeData
            ? "-"
            : waagData.appointments?.mtd && waagData.appointments?.target
            ? `${(
                (waagData.appointments.mtd / waagData.appointments.target) *
                450
              ).toFixed(0)}%`
            : "N/A",
          percentLast: dateRangeData
            ? "-"
            : waagData.appointments?.lastMonth && waagData.appointments?.target
            ? `${(
                (waagData.appointments.lastMonth /
                  waagData.appointments.target) *
                100
              ).toFixed(0)}%`
            : "N/A",
          weeklyData: dateRangeData
            ? dateRangeData.appointments.weeklyData
            : waagData.appointments?.weeklyData || [],
        },
        {
          name: "Total Cancelled Appointments",
          thisWeek: waagData.cancelledAppointments?.thisWeek?.toString() || "0",
          lastWeek: waagData.cancelledAppointments?.lastWeek?.toString() || "0",
          prevWeek: waagData.cancelledAppointments?.prevWeek?.toString() || "0",
          mtd: waagData.cancelledAppointments?.mtd?.toString() || "0",
          lastMonth:
            waagData.cancelledAppointments?.lastMonth?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.cancelledAppointments?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.value,
              date: week.date,
            })) || [],
        },
        {
          name: "Total No Show Appointments",
          thisWeek: waagData.noShowAppointments?.thisWeek?.toString() || "0",
          lastWeek: waagData.noShowAppointments?.lastWeek?.toString() || "0",
          prevWeek: waagData.noShowAppointments?.prevWeek?.toString() || "0",
          mtd: waagData.noShowAppointments?.mtd?.toString() || "0",
          lastMonth: waagData.noShowAppointments?.lastMonth?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.noShowAppointments?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.value,
              date: week.date,
            })) || [],
        },
        {
          name: "Treated Hours",
          thisWeek: waagData.treatedHours?.thisWeek?.toString() || "0",
          lastWeek: waagData.treatedHours?.lastWeek?.toString() || "0",
          prevWeek: waagData.treatedHours?.prevWeek?.toString() || "0",
          mtd: waagData.treatedHours?.mtd?.toString() || "0",
          lastMonth: waagData.treatedHours?.lastMonth?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.treatedHours?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.value,
              date: week.date,
            })) || [],
        },
        {
          name: "Active Clients (30 Days)",
          thisWeek:
            waagData.activeClients?.weeklyData?.[0]?.active30Days?.toString() ||
            "0",
          lastWeek:
            waagData.activeClients?.weeklyData?.[1]?.active30Days?.toString() ||
            "0",
          prevWeek:
            waagData.activeClients?.weeklyData?.[2]?.active30Days?.toString() ||
            "0",
          mtd:
            waagData.activeClients?.weeklyData?.[0]?.active30Days?.toString() ||
            "0",
          lastMonth:
            waagData.activeClients?.weeklyData?.[4]?.active30Days?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.activeClients?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.active30Days,
              date: week.date,
            })) || [],
        },
        {
          name: "Active Clients (60 Days)",
          thisWeek:
            waagData.activeClients?.weeklyData?.[0]?.active60Days?.toString() ||
            "0",
          lastWeek:
            waagData.activeClients?.weeklyData?.[1]?.active60Days?.toString() ||
            "0",
          prevWeek:
            waagData.activeClients?.weeklyData?.[2]?.active60Days?.toString() ||
            "0",
          mtd:
            waagData.activeClients?.weeklyData?.[0]?.active60Days?.toString() ||
            "0",
          lastMonth:
            waagData.activeClients?.weeklyData?.[4]?.active60Days?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.activeClients?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.active60Days,
              date: week.date,
            })) || [],
        },
        {
          name: "New Clients",
          thisWeek: waagData.newClients?.thisWeekNewClients?.toString() || "0",
          lastWeek: waagData.newClients?.lastWeekNewClients?.toString() || "0",
          prevWeek: waagData.newClients?.prevWeekNewClients?.toString() || "0",
          mtd: waagData.newClients?.mtdNewClients?.toString() || "0",
          lastMonth:
            waagData.newClients?.lastMonthNewClients?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.newClients?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.value,
              date: week.date,
            })) || [],
        },
        {
          name: "Client Dropoff",
          thisWeek: waagData.clientDropOff?.thisWeekDropoffs?.toString() || "0",
          lastWeek: waagData.clientDropOff?.lastWeekDropoffs?.toString() || "0",
          prevWeek: waagData.clientDropOff?.prevWeekDropoffs?.toString() || "0",
          mtd: waagData.clientDropOff?.mtdDropoffs?.toString() || "0",
          lastMonth:
            waagData.clientDropOff?.lastMonthDropoffs?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData:
            waagData.clientDropOff?.weeklyData?.map((week) => ({
              weekNumber: week.weekNumber,
              year: week.year,
              value: week.value,
              date: week.date,
            })) || [],
        },
        {
          name: "Intake",
          thisWeek:
            waagData.appointmentTypes?.thisWeek?.[0]?.intake?.count?.toString() ||
            "0",
          lastWeek:
            waagData.appointmentTypes?.lastWeek?.[0]?.intake?.count?.toString() ||
            "0",
          prevWeek:
            waagData.appointmentTypes?.prevWeek?.[0]?.intake?.count?.toString() ||
            "0",
          mtd:
            waagData.appointmentTypes?.mtd?.[0]?.intake?.count?.toString() ||
            "0",
          lastMonth:
            waagData.appointmentTypes?.lastMonth?.[0]?.intake?.count?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData: waagData.appointmentTypes?.weeklyTrendIntake || [],
        },
        {
          name: "Therapy",
          thisWeek:
            waagData.appointmentTypes?.thisWeek?.[0]?.therapy?.count?.toString() ||
            "0",
          lastWeek:
            waagData.appointmentTypes?.lastWeek?.[0]?.therapy?.count?.toString() ||
            "0",
          prevWeek:
            waagData.appointmentTypes?.prevWeek?.[0]?.therapy?.count?.toString() ||
            "0",
          mtd:
            waagData.appointmentTypes?.mtd?.[0]?.therapy?.count?.toString() ||
            "0",
          lastMonth:
            waagData.appointmentTypes?.lastMonth?.[0]?.therapy?.count?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData: waagData.appointmentTypes?.weeklyTrendTherapy || [],
        },
        {
          name: "Assessment",
          thisWeek:
            waagData.appointmentTypes?.thisWeek?.[0]?.assessment?.count?.toString() ||
            "0",
          lastWeek:
            waagData.appointmentTypes?.lastWeek?.[0]?.assessment?.count?.toString() ||
            "0",
          prevWeek:
            waagData.appointmentTypes?.prevWeek?.[0]?.assessment?.count?.toString() ||
            "0",
          mtd:
            waagData.appointmentTypes?.mtd?.[0]?.assessment?.count?.toString() ||
            "0",
          lastMonth:
            waagData.appointmentTypes?.lastMonth?.[0]?.assessment?.count?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData: waagData.appointmentTypes?.weeklyTrendAssessment || [],
        },
        {
          name: "Consult",
          thisWeek:
            waagData.appointmentTypes?.thisWeek?.[0]?.consult?.count?.toString() ||
            "0",
          lastWeek:
            waagData.appointmentTypes?.lastWeek?.[0]?.consult?.count?.toString() ||
            "0",
          prevWeek:
            waagData.appointmentTypes?.prevWeek?.[0]?.consult?.count?.toString() ||
            "0",
          mtd:
            waagData.appointmentTypes?.mtd?.[0]?.consult?.count?.toString() ||
            "0",
          lastMonth:
            waagData.appointmentTypes?.lastMonth?.[0]?.consult?.count?.toString() ||
            "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData: waagData.appointmentTypes?.weeklyTrendConsult || [],
        },
        {
          name: "Phone Calls",
          thisWeek: waagData.phoneCalls?.thisWeek?.toString() || "0",
          lastWeek: waagData.phoneCalls?.lastWeek?.toString() || "0",
          prevWeek: waagData.phoneCalls?.prevWeek?.toString() || "0",
          mtd: waagData.phoneCalls?.mtd?.toString() || "0",
          lastMonth: waagData.phoneCalls?.lastMonth?.toString() || "0",
          target: "0",
          percentCurr: "N/A",
          percentLast: "N/A",
          weeklyData: [],
        },
      ];
      setMetrics(transformedMetrics);
    }
  }, [waagData, dateRangeData]);

  const handleMetricClick = (metricName) => {
    setSelectedMetric(metricName);
  };

  const getSelectedMetricData = () => {
    if (isDateRangeActive) {
      return getDateRangeMetrics().find((m) => m.name === selectedMetric);
    }
    return metrics.find((m) => m.name === selectedMetric);
  };

  return (
    <div className="relative">
      {loadingStatus === "loading" && (
        <div className="absolute inset-0 bg-white/50 z-10 flex justify-center items-center backdrop-blur-sm">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm">
        <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Waag Metrics</h3>
            {isDateRangeActive && (
              <p className="text-sm text-gray-500 mt-0.5">
                {startDate} to {endDate}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showDatePicker ? (
              <form
                onSubmit={handleDateSubmit}
                className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                  <span className="text-sm text-gray-500">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors rounded-sm">
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(false)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors rounded-sm">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                {isDateRangeActive && (
                  <button
                    onClick={handleClearDateRange}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                    Clear Range
                  </button>
                )}
                <button
                  onClick={() => setShowDatePicker(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors rounded-sm">
                  {isDateRangeActive ? "Change Range" : "Select Range"}
                </button>
              </>
            )}
          </div>
        </div>

        {isDateRangeActive ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Metric
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {getDateRangeMetrics().map((metric, index) => (
                  <tr
                    key={metric.name}
                    onClick={() => handleMetricClick(metric.name)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                      index !== getDateRangeMetrics().length - 1 ? 'border-b border-gray-100' : ''
                    }`}>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">
                      {metric.name}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 text-right font-medium">
                      {metric.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Metric
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    This Week
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Last Week
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Prev Week
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    MTD
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Last Month
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Target
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    % Curr
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    % Last
                  </th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric, index) => (
                  <tr
                    key={metric.name}
                    onClick={() => handleMetricClick(metric.name)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                      index !== metrics.length - 1 ? 'border-b border-gray-100' : ''
                    }`}>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">
                      {metric.name}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 font-medium">
                      {metric.thisWeek}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.lastWeek}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.prevWeek}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.mtd}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.lastMonth}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.target}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.percentCurr}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {metric.percentLast}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedMetric && (
        <ChartModal
          metric={getSelectedMetricData()}
          onClose={() => setSelectedMetric(null)}
        />
      )}
    </div>
  );
};

export default WaagChart;
